import type { AccountInfo } from "@azure/msal-browser";

type EntraAccountSubset = Pick<AccountInfo, "username" | "idTokenClaims"> | null | undefined;

interface AccountCandidates {
  username?: string;
  emailClaim?: string;
  emailsClaim: string[];
}

function collectCandidates(account: EntraAccountSubset): AccountCandidates {
  if (!account) return { emailsClaim: [] };
  const claims = account.idTokenClaims as
    | { email?: unknown; emails?: unknown }
    | undefined;
  return {
    username: typeof account.username === "string" ? account.username : undefined,
    emailClaim: typeof claims?.email === "string" ? claims.email : undefined,
    emailsClaim: Array.isArray(claims?.emails)
      ? claims.emails.filter((e): e is string => typeof e === "string")
      : [],
  };
}

// UX gate (not a security check — backend validates the ID token). Returns true when
// the signed-in Entra account corresponds to the email the user typed at the login
// form. Checks `username` plus `email`/`emails` ID-token claims, since B2B guests
// and tenants with UPN != primary SMTP often expose the typed email only via claims.
export function accountMatchesEnteredEmail(
  account: EntraAccountSubset,
  enteredUsername: string | null | undefined,
): boolean {
  if (!account || !enteredUsername) return false;
  const target = enteredUsername.trim().toLowerCase();
  if (!target) return false;

  const { username, emailClaim, emailsClaim } = collectCandidates(account);
  const all = [username, emailClaim, ...emailsClaim].filter(
    (v): v is string => typeof v === "string",
  );
  return all.some((c) => c.trim().toLowerCase() === target);
}

// Returns the best email-shaped identifier for backend lookup or user-facing display.
// Priority: the typed email (when it matched the account, preserving caller intent)
// → `email` claim → first usable entry in `emails` claim → `username`. The fallback
// chain matters for B2B guests where `username` is `user_home.com#EXT#@host.onmicrosoft.com`
// and the real address is only in `idTokenClaims.email`.
export function resolveAccountEmail(
  account: EntraAccountSubset,
  enteredUsername: string | null | undefined,
): string {
  if (enteredUsername && accountMatchesEnteredEmail(account, enteredUsername)) {
    return enteredUsername.trim();
  }
  const { username, emailClaim, emailsClaim } = collectCandidates(account);
  if (emailClaim && emailClaim.trim().length > 0) return emailClaim;
  const firstFromArray = emailsClaim.find((e) => e.trim().length > 0);
  if (firstFromArray) return firstFromArray;
  return username ?? "";
}

// Upper bound on each logged value. enteredEmail is attacker-controlled (raw login-form
// input), so cap every field defensively before it reaches telemetry. A real email/UPN
// is well under this; anything longer is malformed input we don't want to persist whole.
const MAX_TELEMETRY_VALUE_LENGTH = 256;

function clampForTelemetry(value: string): string {
  return value.length > MAX_TELEMETRY_VALUE_LENGTH
    ? value.slice(0, MAX_TELEMETRY_VALUE_LENGTH)
    : value;
}

// Diagnostic snapshot of the account's email-shaped claims and the match outcome,
// for Application Insights. Lets us see what a real tenant's token actually carries —
// e.g. Adani, where the login identifier (UPN = employee number) differs from the
// CMMS-stored email (Graph `mail`). All values are strings (AI custom-property shape).
// NOTE: emails are PII; this is logged intentionally and only when the caller's
// diagnostic flag is on. Values are length-capped (enteredEmail is user-controlled).
export function describeAccountForTelemetry(
  account: EntraAccountSubset,
  enteredUsername: string | null | undefined,
): Record<string, string> {
  const { username, emailClaim, emailsClaim } = collectCandidates(account);
  const fields: Record<string, string> = {
    enteredEmail: (enteredUsername ?? "").trim().toLowerCase(),
    accountUsername: username ?? "",
    emailClaim: emailClaim ?? "",
    emailsClaim: emailsClaim.join(", "),
    hasEmailClaim: String(Boolean(emailClaim)),
    matched: String(accountMatchesEnteredEmail(account, enteredUsername)),
    resolvedEmail: resolveAccountEmail(account, enteredUsername),
  };
  for (const key of Object.keys(fields)) {
    fields[key] = clampForTelemetry(fields[key]);
  }
  return fields;
}
